"use client";

import React from "react";
import { Key, Globe, Building2, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const stages = [{
  title: "Donor Wallet",
  description: "Sichere Übertragung Ihrer digitalen Assets direkt aus Ihrer Wallet. Wir unterstützen alle gängigen Kryptowährungen mit höchster Schweizer Präzision.",
  icon: Key
}, {
  title: "Smart Contract Hub",
  description: "Der 'Swiss Hub' verarbeitet Ihre Spende transparent und unveränderlich. Unser Smart Contract garantiert, dass 100% Ihrer Mittel am Ziel ankommen.",
  icon: Globe
}, {
  title: "Direkter Impact",
  description: "Die Mittel fliessen direkt an verifizierte Stiftungen. Verfolgen Sie den Weg Ihrer Spende in Echtzeit auf der Blockchain – für maximale Transparenz.",
  icon: Building2
}];
function TimelineItem({
  stage,
  index
}: {
  stage: typeof stages[0];
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.2
  });
  const isEven = index % 2 === 0;
  return <div ref={ref} data-index={index} className="relative flex items-center justify-between md:justify-normal group w-full mb-12 md:mb-24 last:mb-0">
      {}
      <div className="absolute left-4 md:left-1/2 -translate-x-1/2 flex items-center justify-center w-12 h-12 rounded-full bg-card border border-primary/30 shadow-sm z-10 transition-all duration-500 group-hover:scale-110 group-hover:border-primary group-hover:shadow-md">
        <stage.icon className="w-5 h-5 text-primary" />
      </div>

      {}
      <div className={`w-[calc(100%-4rem)] ml-auto md:w-[calc(50%-4rem)] transition-all duration-1000 ease-out
          ${isEven ? "md:mr-auto md:ml-0" : "md:ml-auto md:mr-0"}
          ${isVisible ? "opacity-100 translate-x-0 translate-y-0" : "opacity-0 translate-y-8 " + (isEven ? "md:-translate-x-12" : "md:translate-x-12")}
        `}>
        <Card className="bg-white/60 backdrop-blur-md border-border/50 hover:border-primary/30 transition-colors duration-500 relative overflow-hidden group/card shadow-sm">
          {}
          <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-0 group-hover/card:opacity-100 transition-opacity duration-500 pointer-events-none" />
          
          <CardHeader className="pb-4 relative z-10">
            <div className="flex items-center gap-4 mb-2">
              <span className="font-mono text-xs tracking-wider text-primary font-semibold bg-primary/5 px-2 py-1 rounded-md">
                SCHRITT 0{index + 1}
              </span>
            </div>
            <CardTitle className="text-2xl font-default tracking-tight text-foreground">
              {stage.title}
            </CardTitle>
          </CardHeader>
          <CardContent className="relative z-10">
            <CardDescription className="text-base text-muted-foreground leading-relaxed font-default">
              {stage.description}
            </CardDescription>
          </CardContent>
        </Card>
      </div>
    </div>;
}
export default function TheAgileBlueprint() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.5
  });
  return <section data-section-id="223965" id="der-weg-ihrer-spende" className="bg-background text-foreground relative py-24 md:py-32 overflow-hidden">
      {}
      <div className="absolute inset-0 pointer-events-none opacity-[0.03]" style={{
      backgroundImage: "linear-gradient(to right, var(--color-primary) 1px, transparent 1px), linear-gradient(to bottom, var(--color-primary) 1px, transparent 1px)",
      backgroundSize: "60px 60px"
    }} />
      
      {}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-blue-100 rounded-full blur-[120px] pointer-events-none" />

      <div className="container mx-auto px-4 relative z-10">
        {}
        <div ref={headerRef} className={`max-w-3xl mx-auto text-center mb-20 md:mb-32 transition-all duration-1000 ease-out ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h2 className="text-3xl md:text-5xl font-default font-bold mb-6 tracking-tight">
            Der Weg Ihrer <span className="text-primary">Spende</span>
          </h2>
          <p className="text-lg md:text-xl text-muted-foreground font-default">
            Vom Donor bis zur Wirkung: Erfahren Sie, wie Crypto4Good Schweizer Präzision mit modernster Blockchain-Technologie vereint.
          </p>
        </div>

        {}
        <div className="relative max-w-5xl mx-auto">
          {}
          <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px -translate-x-1/2 bg-gradient-to-b from-transparent via-primary/40 to-transparent border-l border-dashed border-primary/30" />

          {}
          <div className="relative py-10">
            {stages.map((stage, index) => <TimelineItem key={index} stage={stage} index={index} />)}
          </div>
        </div>

        {}
        <div className="mt-20 text-center relative z-20">
          <Link to="/DonorsPage">
            <Button size="lg" className="group h-14 px-8 text-base bg-primary hover:bg-primary/90 text-white shadow-md hover:shadow-lg transition-all duration-300 rounded-lg">
              Jetzt Spende starten
              <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Button>
          </Link>
        </div>
      </div>
    </section>;
}